import { LotQuery } from 'components/pages/LotBasePage/types/lotQuery'
import React, { useState } from 'react'
import Tabs, { TabType } from 'components/Tabs'
import { format } from 'date-fns'
import parseISO from 'date-fns/parseISO'
import Image from 'components/Image'
import { ArrowCircleRightIcon, TruckIcon } from '@heroicons/react/solid'
import ForkIcon from 'components/icons/ForkIcon'
import Link from 'next/link'
import Button from 'components/ui/Button'
import FeedbackModal from 'components/pages/LotPage/FeedbackModal'
import cn from 'classnames'
import pathFromUrl from 'utils/pathFromUrl'
import ButtonLink from 'components/ui/ButtonLink'
import { useRouter } from 'next/router'
import { LocationMarkerIcon } from '@heroicons/react/outline'
import stripTimeZone from 'utils/stripTimeZone'

type ScheduleType = NonNullable<LotQuery['lot']>['locationSchedule'][0]
type LotGroupType = NonNullable<LotQuery['lotGroup']>
type LocationType = ScheduleType['locations'][0]

type LocationProps = {
  location: LocationType
  isFirst: boolean
  lotId: number
  truckId: number
  currentPath: string
  lotGroup?: LotGroupType | null
}

const Location = ({
  location,
  isFirst,
  lotId,
  truckId,
  currentPath,
  lotGroup
}: LocationProps) => {
  const [isOpenFeedbackDialog, setIsOpenFeedbackDialog] = useState(false)

  const foodTypes = location.menu.foodTypes
    .map((type) => type.name)
    .slice(0, 4)
    .join(', ')

  const locationPath = `${pathFromUrl(
    location.customerUrl
  )}?previousPage=${encodeURIComponent(
    'Back to all trucks'
  )}&previousPath=${encodeURIComponent(currentPath)}`
  const lotPath = `${location.lot.lotPath}?previousPage=${encodeURIComponent(
    'Back to all locations'
  )}&previousPath=${encodeURIComponent(currentPath)}`

  return (
    <div className="mt-4">
      <div>
        {lotGroup && (
          <Link href={lotPath}>
            <span className="flex items-center mt-2 mb-6 text-2xl md:text-3xl tracking-wide font-extrabold text-gray-800 cursor-pointer hover:underline">
              <div className="inline mr-2">
                <LocationMarkerIcon className="w-7 md:w-8" />
              </div>
              {location.lot.nameWithinGroup}
            </span>
          </Link>
        )}
        <div className="flex gap-x-1 mt-1 items-center">
          <p className="bg-orange-50 border border-solid border-orange-300 text-sm text-orange-600 font-medium rounded-full w-max px-4 py-1">
            <span className="hidden xs:inline">
              {format(parseISO(stripTimeZone(location.startTime)), 'eee ')}
            </span>
            {format(parseISO(stripTimeZone(location.startTime)), 'MM/dd')}
            {', '}
            {format(
              parseISO(stripTimeZone(location.startTime)),
              'h:mmaaa'
            ).toLowerCase()}
            {' - '}
            {format(
              parseISO(stripTimeZone(location.endTime)),
              'h:mmaaa'
            ).toLowerCase()}
          </p>
          <p className="text-gray-600 ml-2 font-bold italic flex items-center">
            <TruckIcon className="text-orange-400 h-5 w-6 mr-1" />
            {location.workStatusHuman}
          </p>
        </div>

        <div className="mt-2">
          <Link href={locationPath}>
            <span className="font-bold text-xl md:text-2xl focus:underline hover:underline">
              {location.truck.name}
            </span>
          </Link>
        </div>

        <div className="mt-2 flex gap-x-2">
          <ForkIcon className="text-orange-400" />
          <p className="text-sm text-gray-600">{foodTypes}</p>
        </div>
      </div>
      <Link
        aria-label={`Order from ${location.truck.name}`}
        href={locationPath}
      >
        <div className="grid grid-cols-2 md:grid-cols-5 md:gap-x-6 gap-x-2 my-6">
          {location.locationItems.slice(0, 4).map((locationItem, i) => (
            <div
              key={locationItem.id}
              className={cn(
                'rounded-md bg-gray-100 flex flex-col cursor-pointer group shadow',
                {
                  'hidden md:flex': i === 3
                }
              )}
            >
              <div className="overflow-hidden">
                <div>
                  <Image
                    alt={locationItem.item.name}
                    className="rounded-t-md"
                    height={600}
                    layout="responsive"
                    priority={isFirst}
                    sizes="25vw"
                    src={locationItem.item.image.high}
                    width={600}
                  />
                </div>
              </div>
              <div className="flex-grow flex items-center justify-center">
                <div className="md:text-base text-xs text-center md:pt-2 pt-1 pb-1 px-2 text-gray-700">
                  {locationItem.item.name}
                </div>
              </div>
            </div>
          ))}
          <div className="group">
            <div className="relative shadow-md hover:shadow-xl transition-all duration-200 rounded-lg box-border bg-white overflow-hidden h-full cursor-pointer">
              <div className="w-full h-full group-hover:scale-105 transition-all duration-500">
                <Image
                  alt={`View more from ${location.truck.name}`}
                  layout="fill"
                  objectFit="cover"
                  priority={isFirst}
                  sizes="(max-width: 768px) 50vw,25vw"
                  src={
                    // 5th item, or the first one if there are less than 5 items in total
                    (location.locationItems[4] ?? location.locationItems[0])
                      .item.image.medium
                  }
                />
              </div>
              <div className="bg-black bg-opacity-40 absolute inset-0 flex justify-center items-center flex-col md:flex-row">
                <p className="text-orange-50 text-lg md:text-2xl font-extrabold group-hover:text-white transition-all duration-200 text-center">
                  Order
                </p>
                <ArrowCircleRightIcon className="md:w-10 md:h-10 w-6 h-6 text-orange-50 md:ml-3 group-hover:text-orange-200  transition-all duration-500" />
              </div>
            </div>
          </div>
        </div>
      </Link>
      <p className="my-8 text-center text-sm font-light text-gray-600">
        Do you have any feedback about {location.truck.name}?{' '}
        <Button
          color="default"
          onClick={() => setIsOpenFeedbackDialog(true)}
          variant="anchor"
        >
          Tell us
        </Button>
      </p>
      <FeedbackModal
        isOpen={isOpenFeedbackDialog}
        lotId={lotId}
        onClose={() => setIsOpenFeedbackDialog(false)}
        truckId={truckId}
      />
    </div>
  )
}

type LocationsProps = {
  locationSchedule: NonNullable<LotQuery['lot']>['locationSchedule']
  lotGroup?: NonNullable<LotQuery['lotGroup']>
  schedulePath: string
}

const Locations = ({
  locationSchedule,
  schedulePath,
  lotGroup
}: LocationsProps) => {
  const router = useRouter()
  const currentPath = router.asPath
  const tabs: TabType<string>[] = locationSchedule.map((s) => ({
    name: s.dateAlias,
    value: s.dateAlias,
    badge: s.locations.length > 0 ? s.locations.length.toString() : null
  }))

  tabs.push({
    name: 'Full Schedule',
    value: '',
    link: schedulePath
  })

  const [currentTabState, setCurrentTab] = useState<TabType>(tabs[0])
  const currentTab = tabs.find((t) => t.value === currentTabState.value)
    ? currentTabState
    : tabs[0]

  const locations = locationSchedule.find(
    (s) => s.dateAlias === currentTab.value
  )?.locations

  if (!locations) {
    return (
      <div className="mx-auto max-w-screen-md lg:max-w-7xl md:px-8 px-4 my-24">
        <div className="relative flex justify-center items-center">
          <TruckIcon className="w-52 h-52 text-orange-50" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 flex flex-col justify-center items-center">
            <div className="italic text-gray-500 font-extralight text-3xl">
              No trucks on this location today
            </div>
          </div>
        </div>
        <div className="justify-center flex">
          <ButtonLink
            color="primary"
            href={schedulePath}
            prefetch={false}
            variant="outline"
          >
            View Full Schedule
          </ButtonLink>
        </div>
      </div>
    )
  }
  return (
    <div>
      <div className="max-w-full w-full py-2 px-2 md:px-0 border-solid border border-gray-100">
        <Tabs
          color="primary"
          currentTab={currentTab}
          setCurrentTab={setCurrentTab}
          shrinkOnMobile={false}
          size="large"
          tabs={tabs}
          variant="contained"
        />
      </div>
      <div className="mx-auto max-w-screen-md lg:max-w-7xl md:px-8 px-4">
        <div className="flex flex-col w-full gap-y-10">
          {locations!.map((location, index) => {
            return (
              <div key={location.id}>
                <Location
                  currentPath={currentPath}
                  isFirst={index === 0}
                  location={location}
                  lotGroup={lotGroup}
                  lotId={location.lot.id}
                  truckId={location.truck.id}
                />
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default Locations
